import { Anchor, Box, Button, Center, Container, Divider, Paper, PasswordInput, Skeleton, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { MIN_PASSWORD } from '../components/change-password';
import { Logo } from '../components/logo';
import { TopLoadBar } from '../components/top-load-bar';

// Where an invitation email lands: the invited address, shown and fixed; a
// name and a password; then the account exists and they sign in as anyone
// does. The link works once, and only while the invitation stands.
export const Route = createFileRoute('/invite')({
  validateSearch: (s: Record<string, unknown>): { invitation_token?: string | undefined } => (typeof s['invitation_token'] === 'string' ? { invitation_token: s['invitation_token'] } : {}),
  component: Invite,
});

interface Lookup { status?: string; reason?: string; email?: string; displayName?: string; organization?: string; existing?: boolean; console?: boolean }
interface Answer { status?: string; reason?: string; message?: string }

const LOOKUP_REASONS: Record<string, string> = {
  invite_used: 'This invitation was already used, so your account exists. Sign in with the password you chose. If you never chose one, use "Forgot it?" on the sign-in page to set it.',
  invite_invalid: 'This invitation is not valid any more. Ask your organization administrator or MotionSafe for a new one.',
  rate_limited: 'Too many attempts from this network. Wait a few minutes before trying again.',
};

function Invite() {
  const { invitation_token: token } = Route.useSearch();
  const lookup = useQuery({
    queryKey: ['invite', token],
    enabled: !!token,
    retry: false,
    queryFn: async (): Promise<Lookup> => {
      const res = await fetch(`/auth/invite?token=${encodeURIComponent(token ?? '')}`, { credentials: 'same-origin' });
      return (await res.json().catch(() => ({ status: 'refused', reason: 'invite_invalid' }))) as Lookup;
    },
  });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const form = useForm({
    // Each field says what is wrong as the person leaves it, not only at the end.
    validateInputOnBlur: true,
    initialValues: { name: '', next: '', again: '' },
    validate: {
      name: (v) => (v.trim() ? null : 'Enter your name.'),
      next: (v) => (v.length >= MIN_PASSWORD ? null : `Use at least ${MIN_PASSWORD} characters.`),
      again: (v, values) => (v === values.next ? null : 'The two passwords differ.'),
    },
  });
  const info = lookup.data?.status === 'ok' ? lookup.data : null;
  // An operator signs in on a device, never here: the page says so from the
  // start, and its last step points there instead of at the sign-in page.
  const operator = !!info && info.console === false;
  const org = info?.organization || 'your organization';
  if (info && !form.values.name && info.displayName && !form.isDirty('name')) form.setFieldValue('name', info.displayName);

  // Someone who already has a MotionSafe login accepts without choosing a
  // password: the one they have stays.
  const accept = async () => {
    if (!token) return;
    setBusy(true);
    try {
      const res = await fetch('/auth/accept-invite', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ token }) });
      const a = (await res.json().catch(() => ({ status: 'refused' }))) as Answer;
      if (a.status === 'ok') setDone(true);
      else setAcceptError(LOOKUP_REASONS[a.reason ?? ''] ?? 'The invitation could not be accepted. Try again in a moment.');
    } catch {
      setAcceptError('The invitation could not be accepted. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };
  const [acceptError, setAcceptError] = useState('');

  const submit = async ({ name, next }: typeof form.values) => {
    if (!token) return;
    setBusy(true);
    try {
      const res = await fetch('/auth/accept-invite', { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ token, password: next, displayName: name.trim() }) });
      const a = (await res.json().catch(() => ({ status: 'refused' }))) as Answer;
      if (a.status === 'ok') { setDone(true); return; }
      form.setFieldError('next', a.reason === 'password_refused' && a.message ? a.message : LOOKUP_REASONS[a.reason ?? ''] ?? 'The account could not be created. Try again in a moment.');
    } catch {
      form.setFieldError('next', 'The account could not be created. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  };

  const refused = lookup.data && lookup.data.status !== 'ok' ? (LOOKUP_REASONS[lookup.data.reason ?? ''] ?? LOOKUP_REASONS['invite_invalid']!) : null;

  return (
    <Box component="main" mih="100dvh" style={{ display: 'grid', gridTemplateRows: '1fr auto' }}>
      <Center p="md">
        <Container size={420} w="100%" p={0}>
          <Stack gap="xs" align="center">
            <Logo height={112} mb={24} />
            <Title order={1} ta="center">{done ? 'Your account is ready' : operator ? 'Join MotionSafe' : 'Join MotionSafe Console'}</Title>
            <Text c="dimmed" size="sm" ta="center">
              {!info ? 'Set up the account you were invited to.'
                : operator ? `You were invited to ${org} as an operator.`
                : info.organization ? `You were invited to administer ${info.organization}.` : 'You were invited to administer an organization.'}
            </Text>
          </Stack>
          <Paper withBorder p={22} mt={30} radius="md" pos="relative">
            {/* Submitting says so along the card's top edge, as the tablet's sign-in card does. */}
            <TopLoadBar active={busy} />
            {done ? (
              <Stack gap="lg">
                <Text>{operator
                  ? `Sign in on ${org}'s device with ${info?.email ?? 'your email'} and ${info?.existing ? 'the password you already have' : 'the password you chose'}. If your organization requires 2FA, the device sets it up at that first sign-in.`
                  : info?.existing
                    ? `Sign in with ${info.email} and the password you already have.`
                    : `Sign in with ${info?.email ?? 'your email'} and the password you chose. If your organization requires 2FA, you set it up at that first sign-in.`}</Text>
                {/* An operator has nothing to sign in to here. */}
                {!operator && <Button component="a" href="/" fullWidth>Sign in</Button>}
              </Stack>
            ) : !token ? (
              <Stack gap="lg">
                <Text>This page needs the link from your invitation email. Open the email and use its link.</Text>
                <Anchor href="/" size="sm">Go to sign-in</Anchor>
              </Stack>
            ) : lookup.isPending ? (
              <Stack gap="lg" aria-busy="true">
                <Skeleton height={44} />
                <Skeleton height={44} />
                <Skeleton height={44} />
              </Stack>
            ) : refused ? (
              <Stack gap="lg">
                <Text>{refused}</Text>
                <Anchor href="/" size="sm">Go to sign-in</Anchor>
              </Stack>
            ) : info?.existing ? (
              <Stack gap="lg">
                <Text>{info.email} already has a MotionSafe account. Accepting adds {info.organization || 'this organization'} to it; your password and 2FA stay as they are.</Text>
                {acceptError && <Text size="sm" c="danger" role="alert">{acceptError}</Text>}
                <Button onClick={() => void accept()} disabled={busy} fullWidth>Accept invitation</Button>
              </Stack>
            ) : (
              // noValidate: the fields carry `required` for the asterisk and for
              // assistive technology, and the form's own sentences do the telling.
              <form noValidate onSubmit={form.onSubmit((v) => void submit(v))}>
                <Stack gap="lg">
                  <Stack gap={4}>
                    <Title order={3}>Create your account</Title>
                    <Text size="sm" c="dimmed">Fields marked <Text span inherit c="danger">*</Text> are required.</Text>
                  </Stack>
                  {/* The address is the invitation's, so it is a fact, not a field. A
                      password manager still reads it as the username of what is saved. */}
                  <Box px={16} py={10} style={{ background: 'var(--mantine-color-body)', border: '1px solid var(--mantine-color-default-border)', borderRadius: 'var(--mantine-radius-md)' }}>
                    <Text size="sm" fw={500}>Signs in with</Text>
                    <Text style={{ overflowWrap: 'anywhere' }}>{info?.email}</Text>
                    <input type="email" name="username" autoComplete="username" value={info?.email ?? ''} readOnly hidden />
                  </Box>
                  <TextInput label="Name" required autoComplete="name" size="md" description="Recorded beside your work in the audit trail." {...form.getInputProps('name')} />
                  <Divider />
                  <PasswordInput label="Password" required description={`At least ${MIN_PASSWORD} characters; longer is better than complicated.`} autoComplete="new-password" size="md" {...form.getInputProps('next')} />
                  <PasswordInput label="Password again" required autoComplete="new-password" size="md" {...form.getInputProps('again')} />
                  <Button type="submit" fullWidth mt={4} disabled={busy}>Create account</Button>
                </Stack>
              </form>
            )}
          </Paper>
        </Container>
      </Center>
      {/* Said to administrators; an operator's own invitation already says where they sign in. */}
      <Box component="footer" p="md" ta="center">
        {!operator && <Text size="xs" c="dimmed">Operators sign in on a device, not here.</Text>}
      </Box>
    </Box>
  );
}
